import { IonButton, IonIcon, IonToast } from "@ionic/react";
import { RefreshContext, useDeviceConnection } from "functions/Context";
import { KeysOf }                              from "functions/FunnyTypes";
import { Glob, useLoc }                        from "functions/Language";
import { FetchOptions, Fetchable, RType, err_key, SupportedDevices, IsFromDevice } from "functions/networking/NetTypes";
import { FC, MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from "react";

import { reloadOutline } from "ionicons/icons";
import { DeviceConnection, dvc_root, FetchListener, responseTopic } from "./DeviceConnection";


type Autoloadable<D extends SupportedDevices> = {
  [ T in Fetchable<D> ] : FetchOptions<D, T> extends null ? T : never
}[Fetchable<D>];

export function useLoader<D extends SupportedDevices, T extends Autoloadable<D>, P>(
  request : T,
  placeholder : P,
	options?: {
		disabled         ?: boolean,
		refr             ?: number | undefined,
		onManualReload   ?: () => void,
		watchOthers      ?: boolean,
		disableWhenLoaded?: boolean,
	}
) {
  const refresh = options?.refr ?? useContext(RefreshContext);
  const mounted = useRef(false);
  const [loading, setLoading] = useState(0);
  const [data, setData] = useState<RType<T, D> | P>(placeholder);
  const [error, setError] = useState<{kind: err_key, msg?: string} | null>(null);
  const nextLoadAfter = useRef<number>(new Date().valueOf());
	const connection = useDeviceConnection();
	const disabled = useRef(options?.disabled ?? false);

  const listener = useRef<FetchListener<D, T>>({
		request: request,
		topic: `${connection.user}/${dvc_root[connection.device._type]}/${connection.device.Mqtt_uid}/${responseTopic}`,
		handler(val) {
			setData(val);

			// možná, že tohle bude dělat neplechu při zobrazování detailu erroru, halvně u timeoutu
			setError(null);
		}
	});

  useEffect(() => {
    mounted.current = true;
    if (options?.watchOthers)
      connection.AddFetchListener(listener.current);
    return () => {
      mounted.current = false;
      connection.RemoveFetchListener(listener.current);
    }
  }, []);

  useEffect(() => {
    if (options?.disabled) {
			disabled.current = true;
      setLoading(0);
		}
		else if (disabled.current) {
			disabled.current = false;
			setLoading(v => v + 1);
    }
  }, [options?.disabled])

	useEffect(() => {
		setLoading(v => v + 1);
	}, [request]);

  useEffect(() => {
    if (refresh > 0 && !options?.disabled && !disabled.current) {
      let requestInProgress = false;

      let cancel = SetAlignedInterval(async () => {
        if (requestInProgress || !mounted.current) return;
        setLoading(v => v + 1);
      }, refresh * 1e3, nextLoadAfter);

      return () => {
        cancel.current();
      }
    } else if (options?.disabled) {
      setLoading(0);
    }
  }, [refresh, options?.disabled, request, disabled.current]);

  useEffect(() => {
		if (loading === 0) return;
		if (disabled.current) {
			setLoading(0);
			return;
		}

    setError(null);

    (async () => {
      let val = await connection.Fetch<D, Autoloadable<D>>(request, null as any);
      if (mounted.current) {
        if (val.err) {
          setError(val);
        } else {
					if (options?.disableWhenLoaded) {
						disabled.current = true;
					}
          setData(val.val);
        }
        setLoading(0);
      }
    })();
  }, [loading]);

  return {
    data,
    loading,
    forceReload() {
      setLoading(v => { disabled.current = false; return v + 1 });
    },
    reload(manual = true) {
      if (!options?.disabled && !disabled.current) {
        setLoading(v => v + 1);
        if (manual)
          options?.onManualReload?.();
      }
    },
    error,
    pause(ms: number) { // remember to unpause by `pause(0)`
      nextLoadAfter.current = ms < 0 ? new Date().setFullYear(9998) : (new Date().valueOf() + ms);
    },
  }
}

export const ReloadButton : FC<{
  loaders : (ReturnType<typeof useLoader>)[]
}> = (props) => {
  const loc = useLoc(glob);
  const eloc = useLoc(err_glob);
  const failed = useMemo<typeof props.loaders>(
    () => {
      return props.loaders.filter(l => l.error !== null);
    },
    [props.loaders]
  );
  const [showErr, setShowErr] = useState(false);
  const [errDetail, setErrDetail] = useState(false);
  const errCloser = useRef<ReturnType<typeof setTimeout> | null>(null);

  function CancelErrClose() {
    if (errCloser.current !== null) {
      clearTimeout(errCloser.current);
      errCloser.current = null;
    }
  }

  //useEffect(() => CancelErrClose, [])

  useEffect(() => {
    if (failed.length > 0) {
      setShowErr(true);
      failed[0]?.pause(5000)
      errCloser.current = setTimeout(() => {
          if (errCloser.current !== null)
            setShowErr(false);
        }, 4000);
    } else {
      setShowErr(false);
    }

    return CancelErrClose;
  }, [failed.length]);

  return <>
    <IonButton onClick={() => props.loaders.forEach(l => l.reload())}>
      {props.loaders.some(l => l.loading)
      ? <IonIcon icon={reloadOutline} id="wtf" className={"spin"} style={{"--tx": "0.6px", "--ty": "-0.023px"}} />
      : <IonIcon icon={reloadOutline} />
      }
    </IonButton>

    <IonToast
      style={{"--white-space": "pre-line", "--button-color": "var(--ion-color-secondary)"}}
      isOpen={showErr}
      position="top"
      message={errDetail && failed[0]?.error
        ? `${loc.error}\n${eloc[failed[0].error.kind]}${failed[0].error?.msg === undefined ? "" : "\n" + failed[0].error.msg}`
        : loc.error}
      layout="stacked"
      buttons={errDetail ? [
        {
          text: loc.close,
          role: "cancel"
        }
      ] : [
        {
          text: loc.more,
          role: "expand",
          handler() {
            CancelErrClose();
            setErrDetail(true);
            failed[0]?.pause(-1);
            return false;
          },
        },
        {
          text: loc.close,
          role: "cancel",
        },
      ]}
      onDidDismiss={() => {
        setErrDetail(false);
        CancelErrClose();
        setShowErr(false);
        failed[0]?.pause(0);
      }}
    />
  </>
}

function SetAlignedInterval(callback: () => PromiseLike<void>, ms: number, soonest: MutableRefObject<number>) {
  let canceled = false;
  const cancel = { current : () => { canceled = true } };

  (async () => {
    while (!canceled) {
      await callback();
      // zdržování kvůli chybové hlášce
      do await new Promise<void>(res => {
          let ref = setTimeout(res, ms - (new Date().valueOf() % ms));
          cancel.current = () => {
            canceled = true;
            clearTimeout(ref);
            res();
          }
        });
      while (new Date().valueOf() < soonest.current)
    }
  })();

  return cancel;
}



const limit = 20;


export function SequentialLoader<D extends SupportedDevices>(props: {
	connection: DeviceConnection<D>,
	refresh : number
}) {
	const loader = useLoader("Online_Short", null, { refr: props.refresh });

	useEffect(() => {
		if (!loader.data) return;
		if (!IsFromDevice(loader.data, props.connection.dvcKind)) return;

		props.connection.seqData.onlineData = loader.data;
		props.connection.seqData.generation++;

		props.connection.seqData.times.push(new Date());
		if (props.connection.seqData.times.length > limit) {
			props.connection.seqData.times.shift();
		}

		for (const k of KeysOf(props.connection.seqData.seqData)) {
			props.connection.seqData.seqData[k].push(loader.data.Data[k as keyof typeof loader.data.Data]);
			if (props.connection.seqData.seqData[k].length > limit) {
				props.connection.seqData.seqData[k].shift();
			}
		}
	}, [loader.data]);

	return <></>
}


const glob = Glob({
	en: {
		error: "Loading failed.",
		more: "Show details",
		close: "Close",
	},
	cs: {
		error: "Načítání selhalo.",
		more: "Zobrazit detaily",
		close: "Zavřít",
	},
	pg: {
		error: "Falha no carregamento",
		more: "Mostrar detalhes",
		close: "Fechar",
	},
	es: {
		error: "Error de carga",
		more: "Mostrar detalles",
		close: "Cerrar",
	}
} as const);

const err_glob = Glob({
	en: {
		canceled: "Request was canceled.",
		parse_fail: "Failed to parse response.",
		send_fail: "Failed to send request.",
		timeout: "Request timed out.",
		unknown: "Unknown error"
	},
	cs: {
		canceled: "Požadavek byl zrušen.",
		parse_fail: "Nepovedlo se přečíst odpověď.",
		send_fail: "Nepovedlo se odeslat požadavek.",
		timeout: "Časový limit požadavku vypršel.",
		unknown: "Neznámá chyba"
	},
	pg: {
		canceled: "A solicitação foi cancelada.",
		parse_fail: "Falha na leitura da resposta.",
		send_fail: "Falha ao enviar a solicitação.",
		timeout: "O tempo limite da solicitação expirou.",
		unknown: "Erro desconhecido",
	},
	es: {
		canceled: "La solicitud ha sido cancelada.",
		parse_fail: "No se puede leer la respuesta.",
		send_fail: "Error al enviar la solicitud.",
		timeout: "Solicitud caducada.",
		unknown: "Error desconocido",
	},
} as const);

